<template lang="pug">
    .product-form.flex-row
        .hidden
            el-form-item(:label="$t('Article')" prop="article.id")
            template(v-if="rules.propertyValueOptions" v-for="(rule, i) in form.propertyValueOptions")
                el-form-item(:label="$t('Property Value Option')" :prop="`propertyValueOptions.${i}.id`")
            //- template(v-if="rules.properties" v-for="(rule, i) in form.properties")
            //-     el-form-item(:label="$t('Property')" :prop="`properties.${i}.id`")
            //-     el-form-item(:label="$t('Property Value')" :prop="`properties.${i}.value.text`")
        figure
            img(v-if="image" :src="image.source.url" :width="image.width" :height="image.height")
        .details.grow
            .flex-row
                .name.grow
                    h3.product-name {{ product.name }}
                    small.product-nr {{ product.nr }}
                        span(v-if="selectedArticle") &nbsp;/ {{ selectedArticle.sku }}
                .price
                    .flex-row
                        .regular-price(:class="{ 'strike-through': salePrice }") {{ price | moneyFormat(currency) }}
                        .sale-price(v-if="salePrice") {{ salePrice | moneyFormat(currency) }}
            template(v-if="product.manage_stock")
                .stock-info(v-if="selectedArticle")
                    .out-of-stock(v-if="selectedArticle.sellable_qty_sum <= 0")
                        i.el-icon-fa-exclamation-triangle.fas
                        | {{ $t('Out of stock') }}
                    .in-stock(v-else v-html="$t('In stock: <b>{qty}</b>', { qty: selectedArticle.sellable_qty_sum })")
                .stock-info(v-else)
                    .out-of-stock(v-if="product.sellable_qty_sum <= 0")
                        i.el-icon-fa-exclamation-triangle.fas
                        | {{ $t('Out of stock') }}
                    .in-stock(v-else v-html="$t('In stock: <b>{qty}</b>', { qty: product.sellable_qty_sum })")
            p(v-if="product.teaser" v-html="product.teaser")
            .property-container
                .property(v-for="pivot in selectablePropertyPivots.filter(el => el.valueOptions && el.valueOptions.length > 1)")
                    property-pivot-values(
                        :key="pivot.property.id"
                        :property="pivot.property"
                        :currency="currency"
                        :value-options="pivot.valueOptions"
                        :selected="getSelectedValueOption(pivot.property)"
                        @click="handlePropertySelection"
                    )
                .input-property(v-for="pivot in selectablePropertyPivots.filter(el => el.property.propType.key === 'input')")
                    el-form-item(
                        v-if="form.properties[pivot.i]"
                        :label="pivot.property.name"
                        :prop="`properties.${pivot.i}.value.text`"
                    )
                        el-input(
                            v-if="pivot.property.input_type === 'number'"
                            v-model="form.properties[pivot.i].value.text"
                            type="number"
                            :min="pivot.property.min_input"
                            :max="pivot.property.max_input"
                        )
                        el-input(
                            v-else-if="pivot.property.input_type === 'textarea'"
                            v-model="form.properties[pivot.i].value.text"
                            type="textarea"
                            :autosize="{ minRows: 5, maxRows: 40 }"
                            :minlength="pivot.property.min_input"
                            :maxlength="pivot.property.max_input"
                            show-word-limit
                        )
                        el-input(
                            v-else-if="pivot.property.input_type === 'textfield'"
                            v-model="form.properties[pivot.i].value.text"
                            type="text"
                            :minlength="pivot.property.min_input"
                            :maxlength="pivot.property.max_input"
                            show-word-limit
                        )
</template>

<i18n>
{
    "en": {
        "In stock: <b>{qty}</b>": "In stock: <b>{qty}</b>"
    },
    "de": {
        "In stock: <b>{qty}</b>": "Auf Lager: <b>{qty}</b>",
        "Out of stock": "Nicht auf Lager"
    }
}
</i18n>

<script>
import uniqid from 'uniqid'

import PropertyPivotValues from '@/extensions/shop/components/Products/PropertyPivotValues'

export default {
    name: 'ProductForm',
    props: {
        product: {
            type: Object,
            required: true,
        },
        articles: {
            type: Array,
            required: true,
        },
        currency: {
            type: String,
            required: true,
        },
        priceType: {
            type: String,
            required: true
        },
        form: {
            type: Object,
            required: true,
        },
        rules: {
            type: Object,
            required: true,
        },
    },
    data: () => ({
        selectedProperties: [],
    }),
    components: {
        PropertyPivotValues
    },
    watch: {
        selectedArticle() {
            if (this.selectedArticle) {
                this.form.article = this.selectedArticle
                this.form.price = this.price
                this.form.sale_price = this.salePrice
            } else {
                this.form.article = undefined
                this.form.price = undefined
                this.form.sale_price = undefined
            }
        },
        price() {
            this.form.price = this.price
        },
        salePrice() {
            this.form.sale_price = this.salePrice
        },
    },
    computed: {
        image() {
            let media = this.product.media || []
            media = _.cloneDeep(media).sort((a, b) => a.position - b.position)

            const el = media.find(media => {
                return media.type === 'image' && media.image !== undefined && media.settings.show.detail
            }) || media.find(media => {
                return media.type === 'image' && media.image !== undefined
            })

            return _.get(el, 'image')
        },
        articlePropertyPivots() {
            return this.product.propertyPivots.filter(el => el.property.article_required === true)
        },
        selectablePropertyPivots() {
            let s = 0
            let i = 0
            return this.product.propertyPivots
                .filter(el => {
                    return el.property.propType.key === 'input'
                        || el.valueOptions.length > 1
                        || el.property.article_required === true
                })
                .map(el => {
                    if (this.articles && this.articles.length) {
                        if (el.property.propType.key === 'input') {
                            el.i = i
                            i++
                        } else {
                            const field = el.property.propType.value_field;
                            el.valueOptions.forEach(v => {
                                const value = v[field];
                                const articles = this.articles.filter(article => {
                                    let match = true;
                                    article.propertyPivots.forEach(ap => {
                                        if (ap.property.id === el.property.id && ap.valueOption[field] !== value) match = false;
                                    });
                                    return match;
                                });

                                if (! articles.length) {
                                    v.disabled = true

                                    const sp = this.selectedProperties.find(sp => sp.property.id === el.property.id);
                                    if (sp && sp.valueOption[field] === value) this.handlePropertySelection(el.property, v);
                                }
                            })

                            el.s = s
                            s++
                        }
                    }
                    return el
                })
        },
        selectedArticle() {
            let articles = this.articles

            if (articles.length > 1) {
                articles = this.articles.filter(article => {
                    let match = true;
                    article.propertyPivots.forEach(ap => {
                        const field = ap.property.propType.value_field;
                        const sp = this.selectedProperties.find(sp => sp.property.id === ap.property.id);
                        if (! sp || ap.valueOption[field] !== sp.valueOption[field]) match = false;
                    });
                    return match;
                });
            }

            if (articles.length === 1) {
                // let article = _.cloneDeep(articles[0]);
                let article = articles[0];
                const stocks = _.get(this.activePointOfSale, 'stocks') || []
                stocks.forEach(stock => {
                    if (! article.stocks.find(el => el.id === stock.id)) {
                        stock = _.cloneDeep(stock);
                        stock.pivot = {
                            id: uniqid('new:'),
                            qty: 0,
                        }
                        this.$set(article.stocks, article.stocks.length, stock);
                        // article.stocks.push(stock);
                    }
                })
                return article;
            }
        },
        price() {
            let price = _.get(this.product.price, `${this.priceType}_value`)
            if (this.selectedArticle) price = _.get(this.selectedArticle.price, `${this.priceType}_value`) || price
            return price + this.surcharge
        },
        salePrice() {
            let price = _.get(this.product.salePrice, `${this.priceType}_value`)
            if (this.selectedArticle) price = _.get(this.selectedArticle.salePrice, `${this.priceType}_value`) || price
            if (price) return price + this.surcharge
        },
        surcharge() {
            let surcharge = 0

            const propertyPivots = _.get(this.product, 'propertyPivots') || []
            propertyPivots.forEach(pivot => {
                if (pivot.valueOptions && pivot.valueOptions.length === 1) {
                    const valueOption = pivot.valueOptions[0]
                    const price = _.get(valueOption, 'pivot.price') || _.get(valueOption, 'price')
                    if (price) surcharge += price[`${this.priceType}_value`]
                }
            })

            this.selectedProperties.forEach(sp => {
                const price = _.get(sp.valueOption, 'pivot.price') || _.get(sp.valueOption, 'price')
                if (price) surcharge += price[`${this.priceType}_value`]
            })

            this.form.properties.forEach(property => {
                const price = _.get(property, 'price')
                if (price && property.value.text) surcharge += price[`${this.priceType}_value`]
            })

            return surcharge
        },
    },
    methods: {
        handlePropertySelection(property, valueOption) {
            const i = this.selectedProperties.findIndex(el => el.property.id === property.id)
            if (i >= 0) {
                if (this.selectedProperties[i].valueOption.id === valueOption.id) this.$delete(this.selectedProperties, i)
                else this.$set(this.selectedProperties, i, { property, valueOption })
            } else {
                this.$set(this.selectedProperties, this.selectedProperties.length, { property, valueOption })
            }

            // this.form.propertyValueOptions = this.selectedProperties.map(el => ({ id: el.valueOption.id }))
            this.selectedProperties.forEach((el, i) => {
                if (el.property.propType.key !== 'input') {
                    this.$set(this.form.propertyValueOptions, i, { id: el.valueOption.id })
                } else {
                    // TODO
                    // this.$set(this.form.properties, i, { id: el.property.id, value: el.valueOption.text })
                }
            })
        },
        getSelectedValueOption(property) {
            const selectedProperty = this.selectedProperties.find(el => el.property.id === property.id)
            if (selectedProperty) return selectedProperty.valueOption
            else return null
        },
    },
    mounted() {
        if (this.selectablePropertyPivots && this.selectablePropertyPivots.length) {
            this.rules.propertyValueOptions = []
            this.rules.properties = []

            this.selectablePropertyPivots.forEach(pivot => {
                if (pivot.property.propType.key !== 'input') {
                    this.form.propertyValueOptions.push({ id: null })
                    this.rules.propertyValueOptions.push({ id: 'required' })
                } else {
                    this.form.properties.push({
                        id: pivot.property.id,
                        input_type: pivot.property.input_type,
                        min_input: pivot.property.min_input,
                        max_input: pivot.property.max_input,
                        price: pivot.property.price,
                        value: {
                            id: uniqid('new:'),
                            text: '',
                        },
                    })

                    let rules = []
                    if (pivot.property.min_input) {
                        rules.push('required')
                        if (pivot.property.input_type === 'number') {
                            rules.push('integer')
                            rules.push('min_value:' + pivot.property.min_input)
                        } else {
                            rules.push('min:' + pivot.property.min_input)
                        }
                    }
                    if (pivot.property.max_input) {
                        if (pivot.property.input_type === 'number') {
                            if (!rules.includes('integer')) rules.push('integer')
                            rules.push('max_value:' + pivot.property.max_input)
                        } else {
                            rules.push('max:' + pivot.property.max_input)
                        }
                    }

                    this.rules.properties.push({
                        id: 'required',
                        value: { text: rules.join('|') },
                    })
                }
            })
        }

        if (this.articlePropertyPivots && this.articlePropertyPivots.length) {
            this.articlePropertyPivots.forEach(pivot => {
                if (pivot.valueOptions && pivot.valueOptions.length === 1) {
                    this.handlePropertySelection(pivot.property, pivot.valueOptions[0])
                }
            })
        }

        if (this.price) this.form.price = this.price
        if (this.salePrice) this.form.sale_price = this.salePrice
    },
}
</script>

<style lang="sass" scoped>
figure
    margin: 0
    img
        background: $color-skeleton
        width: 100px
        height: auto
        display: block
.product-name, .price
    font-size: 1.2rem
    font-weight: bold
    margin: 0

.sale-price
    color: $--color-danger
    font-weight: bold

.stock-info
    margin-top: 1rem
    .out-of-stock i
        color: $--color-danger
        margin-right: 0.5rem

.strike-through
    position: relative
    &:before
        position: absolute
        content: ""
        left: -3px
        top: 50%
        right: -3px
        border-top: 1px solid rgba(0,0,0,0.6)
        // border-color: $--color-info
        transform: rotate(-5deg)

.property-container
    display: flex
    // flex-direction: column
    flex-wrap: wrap
    gap: 1rem
    row-gap: 0.5rem
    margin-top: 1rem
    // .property
    //     margin-top: 0.5rem
    .input-property
        width: 100%
        margin-top: 10px
        .el-form-item::v-deep
            .el-form-item__label
                font-style: italic
                padding-bottom: 3px !important
                min-height: 0 !important
    .property-value::v-deep
        label
            // font-style: inherit
            padding-bottom: 3px
            display: inline-block
</style>
